import { useState, useCallback, useRef, useEffect, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useDroppable,
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { DashboardTab, TabSortBy, TabSortDirection } from '../../api/types'
import {
  useCreateTab,
  useUpdateTab,
  useDeleteTab,
} from '../../api/hooks'
import { createDebug } from '../../utils/debugLog'

const log = createDebug('dw:dashboard-tabs')

// ---------------------------------------------------------------------------
// Sort options
// ---------------------------------------------------------------------------

const SORT_OPTIONS: Array<{ sortBy: TabSortBy; sortDirection: TabSortDirection; labelKey: string }> = [
  { sortBy: 'updated_at', sortDirection: 'desc', labelKey: 'tabBar.sortLastModified' },
  { sortBy: 'updated_at', sortDirection: 'asc', labelKey: 'tabBar.sortOldestModified' },
  { sortBy: 'created_at', sortDirection: 'desc', labelKey: 'tabBar.sortNewestCreated' },
  { sortBy: 'created_at', sortDirection: 'asc', labelKey: 'tabBar.sortOldestCreated' },
  { sortBy: 'name', sortDirection: 'asc', labelKey: 'tabBar.sortNameAZ' },
  { sortBy: 'name', sortDirection: 'desc', labelKey: 'tabBar.sortNameZA' },
  { sortBy: 'manual', sortDirection: 'asc', labelKey: 'tabBar.sortManualOrder' },
]

function SortSelect({
  sortBy,
  sortDirection,
  onSort,
}: {
  sortBy: TabSortBy
  sortDirection: TabSortDirection
  onSort: (sortBy: TabSortBy, dir: TabSortDirection) => void
}) {
  const { t } = useTranslation('settings')
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const currentValue = `${sortBy}:${sortDirection}`
  const currentOption = SORT_OPTIONS.find((o) => `${o.sortBy}:${o.sortDirection}` === currentValue)
  const currentLabel = currentOption ? t(currentOption.labelKey) : t('tabBar.sort')

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (e.target instanceof Node && ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-text-muted hover:text-foreground bg-transparent hover:bg-surface-1 border border-transparent hover:border-border rounded-md transition-colors"
        data-testid="sort-select"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
        </svg>
        {currentLabel}
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 bg-surface-1 border border-border rounded-lg shadow-xl z-50 min-w-[170px] py-1 animate-fade-in" data-testid="sort-menu">
          {SORT_OPTIONS.map((opt) => {
            const isActive = currentValue === `${opt.sortBy}:${opt.sortDirection}`
            return (
              <button
                key={`${opt.sortBy}:${opt.sortDirection}`}
                onClick={() => {
                  onSort(opt.sortBy, opt.sortDirection)
                  setOpen(false)
                }}
                className={`w-full text-left px-3 py-1.5 text-xs font-medium transition-colors flex items-center justify-between ${
                  isActive
                    ? 'text-primary bg-primary/5'
                    : 'text-text-secondary hover:text-foreground hover:bg-surface-2'
                }`}
                data-testid={`sort-option-${opt.sortBy}-${opt.sortDirection}`}
              >
                {t(opt.labelKey)}
                {isActive && (
                  <svg className="w-3 h-3 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Animated delete button
// ---------------------------------------------------------------------------

function TabDeleteButton({ onDelete, tabId }: { onDelete: () => void; tabId: string }) {
  const { t } = useTranslation('settings')
  const [confirming, setConfirming] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (confirming) {
      clearTimeout(timerRef.current)
      setConfirming(false)
      onDelete()
    } else {
      setConfirming(true)
      timerRef.current = setTimeout(() => setConfirming(false), 3000)
    }
  }

  useEffect(() => () => clearTimeout(timerRef.current), [])

  return (
    <button
      onClick={handleClick}
      className={`p-0.5 rounded transition-all duration-200 ${
        confirming
          ? 'text-red-400 bg-red-500/15 scale-125 animate-[wiggle_0.3s_ease-in-out]'
          : 'opacity-0 group-hover:opacity-60 hover:!opacity-100 text-text-muted hover:text-red-400 scale-100'
      }`}
      title={confirming ? t('tabBar.clickAgainToDelete') : t('tabBar.deleteTab')}
      data-testid={`tab-delete-${tabId}`}
    >
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Sortable tab item
// ---------------------------------------------------------------------------

function SortableTab({
  tab,
  isActive,
  onSelect,
  onRename,
  onDelete,
  projectDragActive,
  onProjectHoverTab,
  isDragOverlay,
}: {
  tab: DashboardTab
  isActive: boolean
  onSelect: () => void
  onRename: (name: string) => void
  onDelete: () => void
  projectDragActive?: boolean
  onProjectHoverTab?: (tabId: string | null | undefined) => void
  isDragOverlay?: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState(tab.name)
  const inputRef = useRef<HTMLInputElement>(null)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: tab.id,
    data: { type: 'tab', id: tab.id },
    disabled: editing,
  })

  const { setNodeRef: setProjectHoverRef, isOver: isProjectHoverOver } = useDroppable({
    id: `project-hover-tab-${tab.id}`,
    data: { type: 'project-hover-tab', tabId: tab.id },
  })

  const style = isDragOverlay
    ? {}
    : {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
      }

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const commitRename = () => {
    const trimmed = editName.trim()
    if (trimmed && trimmed !== tab.name) {
      onRename(trimmed)
    } else {
      setEditName(tab.name)
    }
    setEditing(false)
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      commitRename()
    } else if (e.key === 'Escape') {
      setEditName(tab.name)
      setEditing(false)
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      onMouseEnter={() => {
        if (projectDragActive) onProjectHoverTab?.(tab.id)
      }}
      onMouseLeave={() => {
        if (projectDragActive) onProjectHoverTab?.(undefined)
      }}
      className={`group relative border-b-2 -mb-px ${
        isActive
          ? 'border-primary text-foreground font-medium'
          : isProjectHoverOver
            ? 'border-primary/40 text-foreground bg-primary/5'
            : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border'
      } ${isDragging && !isDragOverlay ? 'opacity-30' : ''}`}
      data-testid={`tab-${tab.id}`}
    >
      <div
        ref={setProjectHoverRef}
        className={projectDragActive ? 'absolute inset-0 z-10' : 'hidden'}
        data-project-hover-tab-id={tab.id}
        onPointerEnter={() => onProjectHoverTab?.(tab.id)}
        onPointerLeave={() => onProjectHoverTab?.(undefined)}
        aria-hidden="true"
      />
      <div
        className="relative flex items-center gap-1 px-3 py-2 text-sm cursor-pointer select-none transition-all"
        onClick={() => !editing && onSelect()}
        onDoubleClick={() => {
          setEditName(tab.name)
          setEditing(true)
        }}
        {...attributes}
        {...listeners}
      >
        {editing ? (
          <input
            ref={inputRef}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={commitRename}
            onKeyDown={handleKeyDown}
            className="bg-transparent border-none outline-none text-sm w-24 text-inherit"
            data-testid="tab-rename-input"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="truncate max-w-[120px]" title={tab.name}>
            {tab.name}
          </span>
        )}

        {!editing && <TabDeleteButton onDelete={onDelete} tabId={tab.id} />}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main TabBar export
// ---------------------------------------------------------------------------

export interface DashboardTabBarProps {
  tabs: DashboardTab[]
  activeTabId: string | null
  onTabChange: (tabId: string | null) => void
  allSortBy: TabSortBy
  allSortDirection: TabSortDirection
  onAllSort: (sortBy: TabSortBy, dir: TabSortDirection) => void
  projectDragActive?: boolean
  onProjectHoverTab?: (tabId: string | null | undefined) => void
}

export default function DashboardTabBar({
  tabs,
  activeTabId,
  onTabChange,
  allSortBy,
  allSortDirection,
  onAllSort,
  projectDragActive = false,
  onProjectHoverTab,
}: DashboardTabBarProps) {
  const { t } = useTranslation('settings')
  const createTab = useCreateTab()
  const updateTab = useUpdateTab()
  const deleteTab = useDeleteTab()

  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const createInputRef = useRef<HTMLInputElement>(null)

  const activeTab = activeTabId ? tabs.find((t) => t.id === activeTabId) : null

  const { setNodeRef: setAllProjectHoverRef, isOver: isOverAll } = useDroppable({
    id: 'project-hover-all',
    data: { type: 'project-hover-tab', tabId: null },
  })

  useEffect(() => {
    if (creating && createInputRef.current) {
      createInputRef.current.focus()
    }
  }, [creating])

  const handleCreateTab = useCallback(() => {
    const trimmed = newName.trim()
    if (!trimmed) {
      setCreating(false)
      return
    }
    createTab.mutate(trimmed, {
      onSuccess: (created) => {
        log('tab created: %s (%s)', created.id, trimmed)
        onTabChange(created.id)
      },
    })
    setNewName('')
    setCreating(false)
  }, [newName, createTab, onTabChange])

  const sortBy = activeTab ? activeTab.sort_by : allSortBy
  const sortDirection = activeTab ? activeTab.sort_direction : allSortDirection
  const handleSort = activeTab
    ? (sort_by: TabSortBy, sort_direction: TabSortDirection) => {
        log('tab sort: %s -> %s %s', activeTab.id, sort_by, sort_direction)
        updateTab.mutate({ tabId: activeTab.id, data: { sort_by, sort_direction } })
      }
    : onAllSort

  return (
    <div className="flex items-center border-b border-border mb-6" data-testid="dashboard-tab-bar">
      {/* "All" tab */}
      <div
        className={`relative flex-shrink-0 border-b-2 -mb-px ${
          activeTabId === null
            ? 'border-primary text-foreground font-medium'
            : isOverAll
              ? 'border-primary/40 text-foreground bg-primary/5'
              : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border'
        }`}
        onMouseEnter={() => {
          if (projectDragActive) onProjectHoverTab?.(null)
        }}
        onMouseLeave={() => {
          if (projectDragActive) onProjectHoverTab?.(undefined)
        }}
      >
        <div
          ref={setAllProjectHoverRef}
          className={projectDragActive ? 'absolute inset-0 z-10' : 'hidden'}
          data-project-hover-tab-id="__all__"
          onPointerEnter={() => onProjectHoverTab?.(null)}
          onPointerLeave={() => onProjectHoverTab?.(undefined)}
          aria-hidden="true"
        />
        <button
          onClick={() => onTabChange(null)}
          className="relative px-3 py-2 text-sm transition-all"
          data-testid="tab-all"
        >
          {t('tabBar.all')}
        </button>
      </div>

      <SortableContext items={tabs.map((t) => t.id)} strategy={horizontalListSortingStrategy}>
        {tabs.map((tab) => (
          <SortableTab
            key={tab.id}
            tab={tab}
            isActive={activeTabId === tab.id}
            onSelect={() => onTabChange(tab.id)}
            projectDragActive={projectDragActive}
            onProjectHoverTab={onProjectHoverTab}
            onRename={(name) => {
              log('tab rename: %s -> %s', tab.id, name)
              updateTab.mutate({ tabId: tab.id, data: { name } })
            }}
            onDelete={() => {
              log('tab delete: %s', tab.id)
              if (activeTabId === tab.id) {
                onTabChange(null)
              }
              deleteTab.mutate(tab.id)
            }}
          />
        ))}
      </SortableContext>

      {/* Create new tab */}
      {creating ? (
        <input
          ref={createInputRef}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onBlur={handleCreateTab}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCreateTab()
            if (e.key === 'Escape') {
              setNewName('')
              setCreating(false)
            }
          }}
          placeholder={t('tabBar.tabNamePlaceholder')}
          className="px-3 py-2 text-sm bg-transparent border-b-2 border-primary -mb-px w-28 outline-none text-foreground placeholder:text-text-muted"
          data-testid="tab-create-input"
        />
      ) : (
        <button
          onClick={() => setCreating(true)}
          className="flex-shrink-0 px-2 py-2 text-text-muted hover:text-text-secondary transition-colors border-b-2 border-transparent -mb-px"
          title={t('tabBar.createNewTab')}
          data-testid="tab-create-button"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      )}

      {/* Sort — always visible, right-aligned */}
      <div className="ml-auto flex-shrink-0">
        <SortSelect
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      </div>
    </div>
  )
}
